import {
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    Button,
    Avatar,
    Badge
} from "ncore-ui-kit";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";
import {
    CheckIcon,
    StarIcon,
    BellIcon
} from "../../anatomy/icons";

const NAME = "Badge";

const USAGE_CODE = `<Badge
    icon={CheckIcon}
    type="success"
    title="Active"
/>

<Badge
    variant="solid"
    type="danger"
    title="Late"
/>

<Badge.Indicator
    type="danger"
    count={3}
>
    <Button
        accessibilityLabel="Notifications"
        onPress={handleOpenNotifications}
        variant="tonal"
        icon={BellIcon}
        type="neutral"
    />
</Badge.Indicator>`;

const LINK_CODE = `<Badge
    href="https://example.com/changelog"
    title="What is new"
    type="info"
/>

<Badge
    onPress={handleOpenReleaseNotes}
    title="Release notes"
    variant="outline"
/>`;

const INDICATOR_CODE = `<Badge.Indicator
    count={120}
    max={99}
>
    <Avatar
        title="Ada Lovelace"
    />
</Badge.Indicator>

<Badge.Indicator
    floorBackgroundColor="subtle"
    type="success"
>
    <Avatar
        title="Grace Hopper"
    />
</Badge.Indicator>`;

const TRANSITION_CODE = `<Badge
    transition="morph"
    title={status.title}
    type={status.type}
/>`;

const CUSTOM_CODE = `<Badge
    customTitleColor="onPrimary"
    customIconColor="onPrimary"
    customColor="moon"
    icon={StarIcon}
    title="Moon"
/>`;

const VARIANTS = [
    "tonal",
    "outline",
    "solid",
    "ghost"
] as const;

const TYPES = [
    "primary",
    "neutral",
    "danger",
    "warning",
    "success",
    "info"
] as const;

const SIZES = [
    "small",
    "medium",
    "large"
] as const;

const ICON_POSITIONS = [
    "start",
    "end"
] as const;

const TRANSITIONS = [
    "fade",
    "morph",
    "none"
] as const;

const STATUSES = [
    {
        key: "badgePage.statusQueued",
        type: "neutral"
    },
    {
        key: "badgePage.statusRunning",
        type: "info"
    },
    {
        key: "badgePage.statusSucceeded",
        type: "success"
    },
    {
        key: "badgePage.statusFailed",
        type: "danger"
    }
] as const;

const ROW_STYLE = {
    justifyContent: "center",
    flexWrap: "wrap",
    display: "flex",
    gap: 12
} as const;

const INDICATOR_ROW_STYLE = {
    justifyContent: "center",
    alignItems: "center",
    flexWrap: "wrap",
    display: "flex",
    gap: 32
} as const;

const AVATAR_COUNT_LOCATION = {
    top: -6,
    end: -6
};

const AVATAR_DOT_LOCATION = {
    bottom: 0,
    end: 0
};

const INITIAL_COUNT = 3;

const MAX_COUNT = 99;

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("badgePage.appearanceTitle"),
        id: "appearance"
    },
    {
        title: t("section.sizes"),
        id: "sizes"
    },
    {
        title: t("badgePage.iconTitle"),
        id: "icon"
    },
    {
        title: t("badgePage.linkTitle"),
        id: "link"
    },
    {
        title: t("badgePage.transitionTitle"),
        id: "transition"
    },
    {
        title: t("badgePage.customTitle"),
        id: "custom"
    },
    {
        title: t("badgePage.indicatorTitle"),
        id: "indicator"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const BadgePage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        statusIndex,
        setStatusIndex
    ] = useState(0);

    const [
        count,
        setCount
    ] = useState(INITIAL_COUNT);

    const status = STATUSES[statusIndex] ?? STATUSES[0];

    const handleNextStatus = () => setStatusIndex((statusIndex + 1) % STATUSES.length);

    const handleReadAll = () => setCount(0);

    const handleAddNotification = () => setCount((current) => current + 1);

    return <Article>
        <ComponentHeader
            lead={t("badgePage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("badgePage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("badgePage.overviewCaption")}
            >
                <Badge
                    spreadBehaviour="free"
                    title={t(status.key)}
                    type={status.type}
                />
                <Button
                    title={t("badgePage.nextStatus")}
                    onPress={handleNextStatus}
                    spreadBehaviour="free"
                    variant="outline"
                />
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("badgePage.appearanceTitle")}
            id="appearance"
        >
            <Paragraph>
                {t("badgePage.appearanceText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    VARIANTS.map((variant) => <div
                        style={ROW_STYLE}
                        key={variant}
                    >
                        {
                            TYPES.map((type) => <Badge
                                variant={variant}
                                title={type}
                                type={type}
                                key={type}
                            />)
                        }
                    </div>)
                }
            </Preview>
        </Section>
        <Section
            title={t("section.sizes")}
            id="sizes"
        >
            <Paragraph>
                {t("badgePage.sizesText")}
            </Paragraph>
            <Preview>
                {
                    SIZES.map((size) => <Badge
                        icon={StarIcon}
                        title={size}
                        size={size}
                        key={size}
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("badgePage.iconTitle")}
            id="icon"
        >
            <Paragraph>
                {t("badgePage.iconText")}
            </Paragraph>
            <Preview>
                {
                    ICON_POSITIONS.map((iconPosition) => <Badge
                        iconPosition={iconPosition}
                        spreadBehaviour="free"
                        title={iconPosition}
                        key={iconPosition}
                        icon={CheckIcon}
                        type="success"
                    />)
                }
                <Badge
                    accessibilityLabel={t("badgePage.active")}
                    spreadBehaviour="free"
                    icon={CheckIcon}
                    type="success"
                />
            </Preview>
        </Section>
        <Section
            title={t("badgePage.linkTitle")}
            id="link"
        >
            <Paragraph>
                {t("badgePage.linkText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    VARIANTS.map((variant) => <div
                        style={ROW_STYLE}
                        key={variant}
                    >
                        <Badge
                            title={t("badgePage.linkTitleSample")}
                            href="https://github.com"
                            spreadBehaviour="free"
                            variant={variant}
                        />
                        <Badge
                            title={t("badgePage.linkTitleSample")}
                            href="https://github.com"
                            spreadBehaviour="free"
                            variant={variant}
                            type="primary"
                        />
                    </div>)
                }
            </Preview>
            <CodeBlock
                code={LINK_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("badgePage.transitionTitle")}
            id="transition"
        >
            <Paragraph>
                {t("badgePage.transitionText")}
            </Paragraph>
            <Preview
                caption={t("badgePage.transitionCaption")}
            >
                {
                    TRANSITIONS.map((transition) => <Badge
                        transition={transition}
                        spreadBehaviour="free"
                        title={t(status.key)}
                        type={status.type}
                        key={transition}
                    />)
                }
                <Button
                    title={t("badgePage.nextStatus")}
                    onPress={handleNextStatus}
                    spreadBehaviour="free"
                    variant="outline"
                />
            </Preview>
            <CodeBlock
                code={TRANSITION_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("badgePage.customTitle")}
            id="custom"
        >
            <Paragraph>
                {t("badgePage.customText")}
            </Paragraph>
            <Preview>
                <Badge
                    customTitleColor="onPrimary"
                    customIconColor="onPrimary"
                    spreadBehaviour="free"
                    customColor="moon"
                    icon={StarIcon}
                    title="moon"
                />
                <Badge
                    customTitleColor="onPrimary"
                    customIconColor="onPrimary"
                    spreadBehaviour="free"
                    customColor="info"
                    icon={StarIcon}
                    title="info"
                />
                <Badge
                    customTitleColor="#ffffff"
                    customIconColor="#ffffff"
                    spreadBehaviour="free"
                    customColor="#7c3aed"
                    icon={StarIcon}
                    title="#7c3aed"
                />
            </Preview>
            <CodeBlock
                code={CUSTOM_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("badgePage.indicatorTitle")}
            id="indicator"
        >
            <Paragraph>
                {t("badgePage.indicatorText")}
            </Paragraph>
            <Preview
                caption={t("badgePage.indicatorCaption")}
            >
                <div
                    style={INDICATOR_ROW_STYLE}
                >
                    <Badge.Indicator
                        floorBackgroundColor="mid"
                        count={count}
                        type="danger"
                    >
                        <Button
                            accessibilityLabel={t("badgePage.notifications")}
                            onPress={handleReadAll}
                            variant="tonal"
                            icon={BellIcon}
                            type="neutral"
                        />
                    </Badge.Indicator>
                    <Badge.Indicator
                        floorBackgroundColor="mid"
                        type="danger"
                    >
                        <Button
                            accessibilityLabel={t("badgePage.notifications")}
                            onPress={handleAddNotification}
                            variant="tonal"
                            icon={BellIcon}
                            type="neutral"
                        />
                    </Badge.Indicator>
                    <Badge.Indicator
                        location={AVATAR_COUNT_LOCATION}
                        floorBackgroundColor="mid"
                        count={MAX_COUNT + 21}
                        max={MAX_COUNT}
                        type="danger"
                    >
                        <Avatar
                            title="Ada Lovelace"
                        />
                    </Badge.Indicator>
                    <Badge.Indicator
                        location={AVATAR_DOT_LOCATION}
                        floorBackgroundColor="mid"
                        type="success"
                    >
                        <Avatar
                            title="Grace Hopper"
                        />
                    </Badge.Indicator>
                </div>
                <Button
                    title={t("badgePage.addNotification")}
                    onPress={handleAddNotification}
                    spreadBehaviour="free"
                    variant="outline"
                />
            </Preview>
            <CodeBlock
                code={INDICATOR_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("badgePage.statesText")}
            </Paragraph>
            <Preview>
                {
                    VARIANTS.map((variant) => <Badge
                        title={t("badgePage.disabled")}
                        spreadBehaviour="free"
                        isDisabled={true}
                        variant={variant}
                        icon={StarIcon}
                        key={variant}
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("badgePage.a11yStatic"),
                    t("badgePage.a11yText"),
                    t("badgePage.a11yIconOnly"),
                    t("badgePage.a11yLink"),
                    t("badgePage.a11yIndicator"),
                    t("badgePage.a11yDisabled")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <Paragraph>
                {t("badgePage.propsBadgeIntro")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("badgePage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("badgePage.propIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        description: t("badgePage.propIconPosition"),
                        type: "\"start\" | \"end\"",
                        defaultValue: "\"start\"",
                        name: "iconPosition"
                    },
                    {
                        type: "\"tonal\" | \"outline\" | \"solid\" | \"ghost\"",
                        description: t("badgePage.propVariant"),
                        defaultValue: "\"tonal\"",
                        name: "variant"
                    },
                    {
                        type: "\"primary\" | \"danger\" | \"warning\" | \"neutral\" | \"success\" | \"info\"",
                        description: t("badgePage.propType"),
                        defaultValue: "\"neutral\"",
                        name: "type"
                    },
                    {
                        type: "\"small\" | \"medium\" | \"large\"",
                        description: t("badgePage.propSize"),
                        defaultValue: "\"small\"",
                        name: "size"
                    },
                    {
                        description: t("badgePage.propHref"),
                        type: "string",
                        name: "href"
                    },
                    {
                        description: t("badgePage.propOnPress"),
                        type: "() => void",
                        name: "onPress"
                    },
                    {
                        description: t("badgePage.propTransition"),
                        type: "\"fade\" | \"morph\" | \"none\"",
                        defaultValue: "\"fade\"",
                        name: "transition"
                    },
                    {
                        description: t("badgePage.propSpreadBehaviour"),
                        type: "\"baseline\" | \"free\"",
                        defaultValue: "\"baseline\"",
                        name: "spreadBehaviour"
                    },
                    {
                        description: t("badgePage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        type: "keyof ContainerContentColors | keyof ProjectColorPalette | string",
                        description: t("badgePage.propCustomColor"),
                        name: "customColor"
                    },
                    {
                        type: "keyof TextContentColors | keyof ProjectColorPalette | string",
                        description: t("badgePage.propCustomTitleColor"),
                        name: "customTitleColor"
                    },
                    {
                        type: "keyof IconContentColors | keyof ProjectColorPalette | string",
                        description: t("badgePage.propCustomIconColor"),
                        name: "customIconColor"
                    },
                    {
                        description: t("badgePage.propTitleStyle"),
                        type: "StyleProp<TextStyle>",
                        name: "titleStyle"
                    },
                    {
                        description: t("badgePage.propAccessibilityLabel"),
                        name: "accessibilityLabel",
                        type: "string"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("badgePage.propCustomTheme"),
                        name: "customTheme"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("badgePage.propsNote")}
            </Paragraph>
            <Paragraph>
                {t("badgePage.propsIndicatorIntro")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("badgePage.indicatorPropChildren"),
                        type: "ReactNode",
                        name: "children"
                    },
                    {
                        description: t("badgePage.indicatorPropCount"),
                        type: "number",
                        name: "count"
                    },
                    {
                        description: t("badgePage.indicatorPropMax"),
                        defaultValue: "99",
                        type: "number",
                        name: "max"
                    },
                    {
                        type: "\"primary\" | \"danger\" | \"warning\" | \"neutral\" | \"success\" | \"info\"",
                        description: t("badgePage.indicatorPropType"),
                        defaultValue: "\"neutral\"",
                        name: "type"
                    },
                    {
                        description: t("badgePage.indicatorPropLocation"),
                        type: "{ top, bottom, start, end }",
                        name: "location"
                    },
                    {
                        description: t("badgePage.indicatorPropIsVisible"),
                        defaultValue: "true",
                        name: "isVisible",
                        type: "boolean"
                    },
                    {
                        description: t("badgePage.indicatorPropIsLoading"),
                        defaultValue: "false",
                        name: "isLoading",
                        type: "boolean"
                    },
                    {
                        description: t("badgePage.indicatorPropIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("badgePage.indicatorPropOnPress"),
                        type: "() => void",
                        name: "onPress"
                    },
                    {
                        description: t("badgePage.indicatorPropFloor"),
                        type: "keyof ContainerContentColors | string",
                        name: "floorBackgroundColor"
                    },
                    {
                        description: t("badgePage.indicatorPropSpreadBehaviour"),
                        type: "\"baseline\" | \"free\" | \"stretch\"",
                        defaultValue: "\"baseline\"",
                        name: "spreadBehaviour"
                    },
                    {
                        description: t("badgePage.indicatorPropTitleVariant"),
                        defaultValue: "\"labelSmallSize\"",
                        type: "keyof Typography",
                        name: "titleVariant"
                    },
                    {
                        description: t("badgePage.indicatorPropCustomLocalize"),
                        type: "CustomLocalizeType",
                        name: "customLocalize"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("badgePage.propCustomTheme"),
                        name: "customTheme"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("badgePage.indicatorPropsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default BadgePage;
